import React from "react";
import {
  SimpleShowLayout,
  TextField,
  Record,
  Button,
  TopToolbar,
  FunctionField,
  Labeled,
  ResourceComponentProps,
  Identifier,
  useTranslate,
} from "react-admin";
import MapIcon from "@material-ui/icons/Map";
import GpsFixedIcon from "@material-ui/icons/GpsFixed";
import { Divider, CardContent, Card } from "@material-ui/core";
import { ShareLinksForm } from "./ShareLinksForm";
import { History } from "history";
import resourceNames from "../../../common/data/resourceNames";
import { CurrentLocationDto } from "../../../common/data/service-proxies";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import permissionNames from "../../../common/auth/access-control/permissionNames";

interface Props extends ResourceComponentProps {
  getData: (id: number) => Partial<CurrentLocationDto> | undefined;
  onCancel: () => void;
  id?: any;
}

interface ToolbarProps {
  onCancel: () => void;
  history?: History<any>;
  id: Identifier;
}

const Toolbar = ({ id, onCancel, history, ...props }: ToolbarProps) => {
  const translate = useTranslate();
  const permissions = useActionPermissions();
  return (
    <TopToolbar {...props}>
      {permissions[permissionNames.locations.current.liveTracking] ? (
        <Button
          label={translate(
            "resources.locations-current.labels.goToLiveTracking"
          )}
          onClick={() =>
            history?.push(`/${resourceNames.LOCATIONS_LIVETRACK}/${id}`)
          }
        >
          <GpsFixedIcon />
        </Button>
      ) : null}
      <Button
        label={translate("resources.locations-current.labels.backToMap")}
        onClick={() => onCancel()}
      >
        <MapIcon />
      </Button>
    </TopToolbar>
  );
};

export const CurrentLocationView = ({
  getData,
  onCancel,
  id,
  ...props
}: Props) => {
  const translate = useTranslate();
  const data = getData(parseInt(id));
  const permissions = useActionPermissions();
  return (
    <div className="drawer-content">
      <Toolbar onCancel={onCancel} history={props.history} id={id} />
      <SimpleShowLayout {...props} record={data as Record}>
        <TextField
          source="vehicleName"
          label={translate("resources.locations-current.labels.vehicleId")}
        />
        <TextField
          source="driverFullName"
          label={translate("resources.locations-current.labels.driverName")}
          emptyText={translate(
            "resources.locations-current.labels.unknownDriver"
          )}
        />
        <FunctionField<CurrentLocationDto & Record>
          source="dutyStatus"
          label={translate("resources.locations-current.labels.dutyStatus")}
          render={(record) =>
            record && (
              <TextField
                source="dutyStatus"
                record={{
                  ...record,
                  id: record.id,
                  dutyStatus: translate(
                    `appData.dutyCycles.${record?.dutyStatus}`
                  ),
                }}
              />
            )
          }
        />
        <FunctionField<CurrentLocationDto & Record>
          label={translate("resources.locations-current.labels.drivingSpeed")}
          render={(record) => (
            <TextField
              source="drivingSpeed"
              record={{
                ...record,
                drivingSpeed: `${record?.drivingSpeed ?? "-"} ${
                  record?.drivingSpeedUnit
                }`,
                id: record?.id!,
              }}
            />
          )}
        />
        <TextField
          source="location"
          label={translate("resources.locations-current.labels.geolocation")}
          emptyText="-"
        />
        <TextField
          source="attachedTrailer"
          label={translate("resources.locations-current.labels.trailers")}
          emptyText={translate("resources.locations-current.labels.none")}
        />
      </SimpleShowLayout>
      <Divider />
      {data &&
      permissions[permissionNames.locations.current.manageShareLinks] ? (
        <Card elevation={0}>
          <CardContent>
            <Labeled
              className="share-links-container"
              label={translate("resources.locations-current.labels.shareLinks")}
            >
              <ShareLinksForm data={data as Record} />
            </Labeled>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
